'use client';

/**
 * PremiumTestimonials — V124.2 published testimonials (premium cards).
 *
 * Fetches `GET /api/testimonials/public?userId={userId}&limit=3` — the
 * SAME endpoint used by the basic `profile-testimonials.tsx` (data shape
 * is shared; only the styling differs). Reuses the existing testimonial
 * type structure as the data reference.
 *
 * Premium card styling: dark slate background + cyan-tinted left border +
 * Quote icon accent. Layout: 1 column mobile, 2 columns `md:`, 3 columns
 * `lg:` (max-width 800px centered).
 *
 * Skip behavior: renders `null` while loading + when no testimonials exist
 * (section is optional — silent fail per the basic hub pattern).
 *
 * Anti-P-383: ≤ 150 L. Anti-P-041/042 compliant.
 */

import { useEffect, useState } from 'react';
import { Quote } from 'lucide-react';

interface Testimonial {
  _id: string;
  authorName: string;
  authorTier: string;
  authorCountry: string;
  testimonialText: string;
  monthsActive: number;
}

export interface PremiumTestimonialsProps {
  userId: string;
}

export function PremiumTestimonials({ userId }: PremiumTestimonialsProps) {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Defense-in-depth: the public API expects a 24-char ObjectId. Skip
    // fetch for malformed/empty ids (silent fail — testimonials are
    // non-critical, same pattern as the basic `profile-testimonials.tsx`).
    if (!userId || userId.length !== 24) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    fetch(`/api/testimonials/public?userId=${encodeURIComponent(userId)}&limit=3`, {
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((body) => setTestimonials(body.data ?? []))
      .catch(() => {
        // Silent — analytics must never break the UX.
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [userId]);

  if (loading || testimonials.length === 0) return null;

  return (
    <section
      aria-labelledby="premium-testimonials-heading"
      className="px-6 sm:px-8 mt-8"
    >
      <h2
        id="premium-testimonials-heading"
        className="text-sm font-semibold text-white inline-flex items-center gap-2 mb-4"
      >
        <Quote className="size-4 text-[#06B6D4]" aria-hidden="true" />
        Lo que dicen de mí
      </h2>
      <div className="mx-auto max-w-[800px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {testimonials.map((t) => (
          <div
            key={t._id}
            className="p-4 rounded-xl bg-slate-900/60 border-l-2 border-[#06B6D4] border-y border-r border-slate-700/60"
          >
            <Quote className="size-4 text-[#06B6D4]/60 mb-2" aria-hidden="true" />
            <p className="text-sm text-slate-200 italic leading-relaxed">
              &ldquo;{t.testimonialText}&rdquo;
            </p>
            <div className="mt-3 text-xs">
              <span className="font-semibold text-white">{t.authorName}</span>
              <span className="text-slate-400">
                {' '}{t.authorTier ? `· ${t.authorTier}` : ''}
                {t.authorCountry ? ` · ${t.authorCountry}` : ''}
                {t.monthsActive > 0 ? ` · ${t.monthsActive} meses activo` : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
